/**
 * Register Page with purposeful animations
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePageLoadAnimation } from '../../hooks/useAnimation';

export const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  
  const { register, error, clearError } = useAuth();
  const navigate = useNavigate();
  const isLoaded = usePageLoadAnimation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setFormError(null);
    
    if (!username || !email || !password) {
      setFormError('All fields are required');
      return;
    }
    
    if (username.length < 3) {
      setFormError('Username must be at least 3 characters');
      return;
    }
    
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await register({ username, email, password });
      navigate('/');
    } catch (err) {
      // Error handled by context
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = formError || error;

  const inputClasses = (fieldName: string) => `
    w-full px-4 py-3 bg-studio-900 border-2 rounded-lg
    text-stone-200 placeholder-stone-600
    focus:outline-none focus:border-amber-500/50 transition-all duration-200
    ${focusedField === fieldName ? 'border-amber-500/50' : 'border-studio-700'}
  `;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-warm py-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div 
          className="text-center mb-10"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(-20px)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1)'
          }}
        >
          <Link to="/" className="inline-flex items-baseline gap-1 group">
            <span className="font-display text-3xl font-semibold text-stone-100 group-hover:text-amber-500 transition-colors duration-300">
              Rhythm
            </span>
            <span className="font-display text-3xl font-semibold text-amber-500 italic">
              Sense
            </span>
          </Link>
          <p className="mt-3 text-stone-500">Create your account to start practicing</p>
        </div>
        
        {/* Form Card */}
        <div 
          className="card-editorial rounded-2xl p-8"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.98)',
            transition: 'all 0.6s cubic-bezier(0.22, 1, 0.36, 1) 100ms'
          }}
        >
          <form className="space-y-5" onSubmit={handleSubmit}>
            {displayError && (
              <div 
                className="bg-coral-400/10 border border-coral-400/30 text-coral-400 px-4 py-3 rounded-lg text-sm"
                style={{ animation: 'shake 0.5s ease-in-out' }}
              >
                {displayError}
              </div>
            )}
            
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-stone-400 mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setFocusedField('username')}
                onBlur={() => setFocusedField(null)}
                className={inputClasses('username')}
                style={{
                  transform: focusedField === 'username' ? 'scale(1.01)' : 'scale(1)',
                  boxShadow: focusedField === 'username' ? '0 0 0 4px rgba(245, 166, 35, 0.1)' : 'none'
                }}
                placeholder="johndoe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-stone-400 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                className={inputClasses('email')}
                style={{
                  transform: focusedField === 'email' ? 'scale(1.01)' : 'scale(1)',
                  boxShadow: focusedField === 'email' ? '0 0 0 4px rgba(245, 166, 35, 0.1)' : 'none'
                }}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-stone-400 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                className={inputClasses('password')}
                style={{
                  transform: focusedField === 'password' ? 'scale(1.01)' : 'scale(1)',
                  boxShadow: focusedField === 'password' ? '0 0 0 4px rgba(245, 166, 35, 0.1)' : 'none'
                }}
                placeholder="••••••••"
              />
              <p className="mt-1.5 text-xs text-stone-600">Must be at least 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-stone-400 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className={inputClasses('confirmPassword')}
                style={{
                  transform: focusedField === 'confirmPassword' ? 'scale(1.01)' : 'scale(1)',
                  boxShadow: focusedField === 'confirmPassword' ? '0 0 0 4px rgba(245, 166, 35, 0.1)' : 'none'
                }}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-amber-500 text-studio-950 font-semibold rounded-lg
                         hover:bg-amber-400 transition-all duration-200
                         disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
              style={{ transform: isSubmitting ? 'scale(0.98)' : 'scale(1)' }}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-studio-800 text-center">
            <p className="text-sm text-stone-500">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-amber-500 hover:text-amber-400 transition-colors relative group">
                Sign in
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full" />
              </Link>
            </p>
          </div>
        </div>

        {/* Back link */}
        <div 
          className="text-center mt-6"
          style={{ 
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) 200ms'
          }}
        >
          <Link to="/" className="text-stone-500 hover:text-stone-300 text-sm transition-colors inline-flex items-center gap-2 group">
            <span className="transition-transform duration-200 group-hover:-translate-x-1">←</span>
            Back to home
          </Link>
        </div>
      </div>
      
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

export default Register;
